import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { FullScreenBackground } from "../../compoment/PortraitStage";
import newBg from "../../assets/img/new-bg.png";
import typoText from "../../assets/img/typo-text.png";
import cardBtn from "../../assets/img/card-bth.png";
import UsernameModal from "../../modal/UsernameModal";
import WisdomSubmitModal, { WisdomSubmitPhase } from "../../modal/WisdomSubmitModal";
import { useSocket } from "../../context/SocketContext";
import Loader from "../../compoment/ui/Loader";
import { updateTicketNamesApi } from "../../services/updateTicketNamesApi";
import { splitName } from "../../utils";
import { toast } from "react-toastify";
import { checkTreeOfWisdomContentApi, resolveNextCategoryInSequence } from "../../api";
import { CategoryItem } from "../../utils/categorySequence";
import { ArrowRight, CircleBack, ErrorAlert, ShieldCheck } from "../../compoment/icon";
import { useRouteFrozen } from "../../routes/routeTransition";

const MainScreen = () => {
    const navigate = useNavigate();
    const routeFrozen = useRouteFrozen();
    const [wisdom, setWisdom] = useState("");
    const [wisdomErr, setWisdomErr] = useState("");
    const [showPreview, setShowPreview] = useState(false);
    const [returningToEdit, setReturningToEdit] = useState(false);
    const [showNurture, setShowNurture] = useState(false);
    const [submitPhase, setSubmitPhase] = useState<WisdomSubmitPhase | null>(null);
    const [checkingContent, setCheckingContent] = useState(false);
    const { socket, connected } = useSocket();
    /** Tracks an in-flight YES SUBMIT socket emit so blocked/error can cancel it. */
    const confirmWatch = useRef(0);
    const submitTimers = useRef<number[]>([]);
    const location = useLocation() as {
        state?: { category?: string; categoryObject?: CategoryItem };
    };

    const [loading, setLoading] = useState(false);
    const [ticketData, setTicketData] = useState<any>({});
    const [nameModal, setNameModal] = useState(false);
    const [name, setName] = useState("");
    const [nameErr, setNameErr] = useState("");
    const [category, setCategory] = useState(location?.state?.category || "");
    const [categoryObject, setCategoryObject] = useState<CategoryItem | null>(
        location?.state?.categoryObject || null
    );

    useEffect(() => {
        const storedData = localStorage.getItem("ticket_detail");
        if (storedData) {
            try {
                setTicketData(JSON.parse(storedData));
            } catch (error) {
                console.error("Error parsing ticket_detail from localStorage:", error);
            }
        }
    }, []);

    useEffect(() => {
        const navigationState = window.history.state;
        if (ticketData?.first_name && ticketData?.last_name) {
            setName(`${ticketData.first_name} ${ticketData.last_name}`.trim());
            setNameModal(false);
        } else if (navigationState?.userName) {
            setName(navigationState.userName);
            setNameModal(false);
        } else if (ticketData?.id) {
            setNameModal(true);
        }
    }, [ticketData]);

    // Fallback: if user lands here without a category, assign the next in sequence
    useEffect(() => {
        if (routeFrozen || (category && categoryObject)) return;

        let cancelled = false;
        const assignCategory = async () => {
            try {
                setLoading(true);
                const nextCategory = await resolveNextCategoryInSequence();
                if (cancelled) return;

                if (!nextCategory) {
                    toast.error("Unable to assign a category. Please try again.");
                    navigate("/qr-scan", { replace: true });
                    return;
                }

                setCategory(nextCategory.name);
                setCategoryObject(nextCategory);
            } catch (error) {
                console.error("Failed to resolve next category:", error);
                if (!cancelled) {
                    toast.error("Unable to assign a category. Please try again.");
                    navigate("/qr-scan", { replace: true });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        assignCategory();
        return () => {
            cancelled = true;
        };
    }, [category, categoryObject, navigate, routeFrozen]);

    const handleSubmitName = async () => {
        if (!name.trim()) {
            setNameErr("Name field is required.");
            return;
        }
        setNameErr("");
        const { firstName, lastName } = splitName(name);

        if (!ticketData?.id) {
            toast.error("Invalid ticket data.");
            return;
        }

        try {
            setLoading(true);
            const response = await updateTicketNamesApi(ticketData.id, {
                first_name: firstName,
                last_name: lastName || " ",
            });

            if (response?.success) {
                const updatedTicket = {
                    ...ticketData,
                    first_name: firstName,
                    last_name: lastName || " ",
                };
                localStorage.setItem("ticket_detail", JSON.stringify(updatedTicket));
                setTicketData(updatedTicket);
                toast.success("Ticket name updated successfully.");
            } else {
                console.error("Failed to update ticket names:", response?.error);
                toast.error(response?.message || "Error updating ticket names.");
            }
        } catch (error) {
            console.error("Error updating ticket names:", error);
            toast.error("Something went wrong while updating the ticket name.");
        } finally {
            setLoading(false);
            setNameModal(false);
        }
    };

    const handleWisdomChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        if (value.length <= 80) {
            setWisdom(value);
            if (wisdomErr) setWisdomErr("");
            if (showNurture) setShowNurture(false);
        }
    };

    useEffect(() => {
        const returned = location.state as { showNurture?: boolean; wisdom?: string } | undefined;
        if (!returned?.showNurture) return;
        if (returned.wisdom) setWisdom(returned.wisdom);
        setShowPreview(false);
        setShowNurture(true);
    }, [location.state]);

    useEffect(() => {
        return () => {
            submitTimers.current.forEach((timer) => window.clearTimeout(timer));
        };
    }, []);

    useEffect(() => {
        if (!socket) return;

        const rejectConfirm = () => {
            if (!confirmWatch.current) return;
            confirmWatch.current = 0;
            submitTimers.current.forEach((timer) => window.clearTimeout(timer));
            submitTimers.current = [];
            setSubmitPhase(null);
            setShowPreview(false);
            setShowNurture(true);
        };

        const onError = () => {
            rejectConfirm();
        };

        /** Final submit was blocked on the server — cancel animation and show nurture. */
        const onBlocked = (payload?: {
            type?: string;
            reason?: string;
            matchedWord?: string | null;
            blockedBy?: string | null;
            moderationReason?: string | null;
        }) => {
            console.log("Received treeOfWisdomBlocked:", payload);
            rejectConfirm();
        };

        socket.on("error", onError);
        socket.on("treeOfWisdomBlocked", onBlocked);
        return () => {
            socket.off("error", onError);
            socket.off("treeOfWisdomBlocked", onBlocked);
        };
    }, [socket]);

    const buildTicketInfo = () => {
        try {
            const storedTicket = localStorage.getItem("ticket_detail");
            return storedTicket ? JSON.parse(storedTicket) : null;
        } catch (error) {
            console.warn("Unable to parse ticket_detail from localStorage", error);
            return null;
        }
    };

    /** Edit screen: dry-run blocked-words check only — no socket submit. */
    const handleNext = async () => {
        if (checkingContent) return;

        if (!wisdom.trim()) {
            setWisdomErr("Please enter your wisdom text.");
            return;
        }
        if (wisdom.length > 80) {
            setWisdomErr("Wisdom text cannot exceed 80 characters.");
            return;
        }
        if (!category) {
            toast.error("Category is not ready yet. Please wait a moment.");
            return;
        }

        setCheckingContent(true);
        try {
            const result = await checkTreeOfWisdomContentApi(wisdom.trim());
            if (result.blocked) {
                setShowPreview(false);
                setShowNurture(true);
                return;
            }
            setShowNurture(false);
            setShowPreview(true);
        } catch (error) {
            console.warn("Unable to check wisdom content", error);
            toast.error("Unable to validate your wisdom. Please try again.");
        } finally {
            setCheckingContent(false);
        }
    };

    const focusWisdom = () => {
        window.setTimeout(() => {
            document.getElementById("wisdomTxt")?.focus();
        }, 0);
    };

    const handleEditWisdom = () => {
        confirmWatch.current = 0;
        setShowPreview(false);
        setShowNurture(false);
        setReturningToEdit(true);
        focusWisdom();
    };

    /** Confirm screen: real socket submit that persists + broadcasts. */
    const handleConfirmSubmit = () => {
        if (submitPhase) return;

        if (!socket || !connected) {
            setShowNurture(true);
            return;
        }

        const ticketInfo = buildTicketInfo();
        const magicState = {
            wisdom,
            category,
            categoryObject,
            ticketInfo,
        };

        const watchId = Date.now();
        confirmWatch.current = watchId;
        socket.emit("updateTreeOfWisdomStatus", {
            status: "in_use",
            wisdom,
            category,
            categoryObject,
            ticketInfo,
        });

        setSubmitPhase("submitting");
        submitTimers.current.forEach((timer) => window.clearTimeout(timer));
        submitTimers.current = [
            window.setTimeout(() => {
                // Blocked path clears confirmWatch to 0 — skip animation advance.
                if (confirmWatch.current !== watchId) return;
                setSubmitPhase("submitted");
            }, 2000),
            window.setTimeout(() => {
                if (confirmWatch.current !== watchId) return;
                confirmWatch.current = 0;
                navigate("/magic", { state: magicState });
            }, 4500),
        ];
    };

    return (
        <>
            {(loading || checkingContent) && <Loader />}
            <FullScreenBackground image={newBg} position="center center" />
            <section className="mainSection">
                <button
                    type="button"
                    className="scanScreen__back"
                    aria-label="Back"
                    onClick={() => navigate("/qr-scan", { replace: true, state: { rescan: Date.now() } })}
                >
                    <CircleBack />
                </button>
                <div className="reasonSection">
                    <img className="wisdomLogo" src={typoText} alt="Tree of Wisdom" />
                    <p className="wisdomTagline">Share your wisdom to the world!</p>
                    <div
                        className={`wisdomCard${returningToEdit && !showPreview ? " wisdomCard--return" : ""}`}
                        onAnimationEnd={(event) => {
                            if (event.target !== event.currentTarget) return;
                            if (event.animationName !== "wisdomCardReturn") return;
                            setReturningToEdit(false);
                        }}
                    >
                        {showPreview ? (
                            <div className="wisdomPreview">
                                <p className="wisdomPreview__label">YOUR WISDOM:</p>
                                <div className="wisdomPreview__quote">“{wisdom}”</div>
                                <h2 className="wisdomPreview__ready">READY TO SUBMIT YOUR WISDOM?</h2>
                                <div className="wisdomPreview__actions">
                                    <button
                                        type="button"
                                        className="wisdomPreview__yes"
                                        style={{ backgroundImage: `url(${cardBtn})` }}
                                        onClick={handleConfirmSubmit}
                                    >
                                        YES, SUBMIT
                                    </button>
                                    <button type="button" className="wisdomPreview__edit" onClick={handleEditWisdom}>
                                        KEEP EDITING
                                    </button>
                                </div>
                            </div>
                        ) : (
                        <>
                        <h1 className="wisdomCard__title">Your wisdom</h1>
                        <p className="wisdomCard__subtitle">A few words can inspire someone.</p>
                        <textarea
                            name="wisdomTxt"
                            id="wisdomTxt"
                            placeholder="What wisdom would you like to share?"
                            value={wisdom}
                            onChange={handleWisdomChange}
                            className={showNurture ? "is-invalid" : undefined}
                            aria-invalid={showNurture || undefined}
                        />
                        <div className="wisdomCard__meta">
                            <span>Maximum of 80 characters only</span>
                            <span>{wisdom.length}/80</span>
                        </div>
                        {wisdomErr && <small className="text-danger">{wisdomErr}</small>}
                        {showNurture && (
                            <p className="wisdomCard__profane" role="alert">
                                <ErrorAlert />
                                <span>Your text contains foul or profane language. Please revise your wisdom and try again.</span>
                            </p>
                        )}
                        <button
                            type="button"
                            className="wisdomCard__submit"
                            style={{ backgroundImage: `url(${cardBtn})` }}
                            onClick={handleNext}
                            disabled={checkingContent}
                        >
                            {checkingContent ? "Checking…" : "Submit wisdom"}
                            {!checkingContent && <ArrowRight width={32} height={32} />}
                        </button>
                        <div className="wisdomNotice">
                            <div className="wisdomNotice__head">
                                <ShieldCheck />
                                <strong>Keep it kind and respectful</strong>
                            </div>
                            <ul>
                                <li>No profanity, hate speech, or threats.</li>
                                <li>No sexual content or illegal activity.</li>
                                <li>No personal or private information.</li>
                            </ul>
                        </div>
                        <p className="wisdomCard__public">Your message will be displayed publicly.</p>
                        </>
                        )}
                    </div>
                </div>
            </section>

            {submitPhase && <WisdomSubmitModal show phase={submitPhase} />}

            {nameModal && (
                <UsernameModal
                    show={nameModal}
                    onHide={() => setNameModal(false)}
                    handleSubmit={handleSubmitName}
                    name={name}
                    setName={setName}
                    nameErr={nameErr}
                    setNameErr={setNameErr}
                />
            )}
        </>
    );
};

export default MainScreen;
